import { AnimatePresence, motion } from 'framer-motion';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ForwardRefRenderFunction,
  type ReactNode,
} from 'react';
import ActiveIcon from '../../images/carousel/carousel-active.svg';
import InactiveIcon from '../../images/carousel/carousel-inactive.svg';
import '../../themes/carousel.scss';

export interface ICarouselItem {
  [key: string]: unknown;
}

export interface IRenderItemParams<T extends ICarouselItem> {
  item: T;
  index: number;
}

interface ICarouselProps<T extends ICarouselItem = any> {
  /** Array of data items to display in the carousel */
  data: T[];
  /** Function to render each carousel item */
  renderItem: (params: IRenderItemParams<T>) => ReactNode;
  /** Whether the carousel should loop infinitely */
  loop?: boolean;
  /** Callback fired when carousel snaps to a new item */
  onSnapToItem?: (index: number) => void;
  /** Custom styles for the carousel container */
  containerCustomStyle?: CSSProperties;
}

export interface ICarouselRef {
  /** Navigate to the next slide */
  goToNext: () => void;
  /** Navigate to the previous slide */
  goToPrev: () => void;
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
  }),
};

const slideTransition = {
  x: { type: 'spring' as const, stiffness: 300, damping: 30 },
  opacity: { duration: 0.2 },
};

/**
 * Carousel Component
 *
 * A modern carousel/slider component with smooth animations, navigation dots,
 * and optional looping functionality. Supports both automatic and manual navigation
 * with framer-motion powered transitions.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for the NewBadgeAnnouncementPopup component.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/NewBadgeAnnouncementPopup/Carousel
 * - src/components/shared/Carousel
 *
 * If this is truly only used for badge announcements, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * const data = [{ id: 1, content: 'Slide 1' }, { id: 2, content: 'Slide 2' }];
 *
 * <Carousel
 *   data={data}
 *   renderItem={({ item, index }) => <div>{item.content}</div>}
 *   loop={true}
 *   onSnapToItem={(index) => console.log('Current slide:', index)}
 * />
 * ```
 */
const CarouselComponent: ForwardRefRenderFunction<ICarouselRef, ICarouselProps> = (
  { data, renderItem, loop = false, onSnapToItem, containerCustomStyle },
  ref,
) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const directionRef = useRef(1); // Store the direction of movement
  const sliderRef = useRef<HTMLDivElement>(null); // Ref to access the slider element

  // Allow parent to call next/prev methods via ref
  useImperativeHandle(ref, () => ({
    goToNext: handleNext,
    goToPrev: handlePrev,
  }));

  const updateIndex = (newIndex: number) => {
    setCurrentIndex(newIndex);
    directionRef.current = newIndex > currentIndex ? 1 : -1;
    onSnapToItem?.(newIndex);
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    updateIndex(loop && nextIndex >= data.length ? 0 : Math.min(nextIndex, data.length - 1));
  };

  const handlePrev = () => {
    const prevIndex = currentIndex - 1;
    updateIndex(loop && prevIndex < 0 ? data.length - 1 : Math.max(prevIndex, 0));
  };

  const handleDotClick = (index: number) => {
    if (index !== currentIndex) updateIndex(index);
  };

  // Reset scroll position on slide change
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.scrollTop = 0;
    }
  }, [currentIndex]);

  return (
    <div className="carousel" style={containerCustomStyle}>
      <div className="carousel-controls">
        <div className="carousel-slider" ref={sliderRef}>
          <AnimatePresence initial={false} custom={directionRef.current}>
            {data.length > 0 && (
              <motion.div
                key={currentIndex}
                custom={directionRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={slideTransition}
                className="carousel-slide"
              >
                {renderItem({ item: data[currentIndex], index: currentIndex })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="carousel-dots">
          {data.map((_, index) => (
            <img
              key={index}
              src={currentIndex === index ? ActiveIcon : InactiveIcon}
              alt="carousel-dot"
              className="carousel-dot"
              onClick={() => handleDotClick(index)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Carousel = forwardRef(CarouselComponent);
